import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'
import type { Lang } from '@/i18n/config'

import Head from 'next/head'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { Cairo, Inter, Poppins } from 'next/font/google'
import { DirectionProvider } from '@base-ui/react/direction-provider'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import { RouteProgress } from '@/components/generals/route-progress'
import { toDir, toLang } from '@/i18n/config'

const cairo = Cairo({
    subsets: ['arabic', 'latin'],
    display: 'swap'
})

const inter = Inter({
    subsets: ['latin'],
    display: 'swap',
    preload: false
})

const poppins = Poppins({
    subsets: ['latin'],
    weight: ['500', '600', '700'],
    display: 'swap',
    preload: false
})

/** Arabic reads in Cairo; English reads in Inter with Poppins headings. Each keeps the other as a fallback for mixed text. */
const fontVariables = (lang: Lang) => {
    const sans = lang === 'ar'
        ? `${cairo.style.fontFamily}, ${inter.style.fontFamily}`
        : `${inter.style.fontFamily}, ${cairo.style.fontFamily}`
    const heading = lang === 'ar'
        ? cairo.style.fontFamily
        : `${poppins.style.fontFamily}, ${cairo.style.fontFamily}`

    return `:root{--font-app-sans:${sans};--font-app-mono:ui-monospace,SFMono-Regular,Menlo,monospace;--font-app-heading:${heading}}`
}

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const { locale } = useRouter()
    const lang = toLang(locale)
    const dir = toDir(lang)

    useEffect(() => {
        document.documentElement.lang = lang
        document.documentElement.dir = dir
    }, [lang, dir])

    return (
        <DirectionProvider direction={dir}>
            <TooltipProvider>
                <div dir={dir} lang={lang} className='min-h-full font-sans'>
                    <Head>
                        <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
                        <style key='afia-fonts' dangerouslySetInnerHTML={{ __html: fontVariables(lang) }} />
                    </Head>

                    <RouteProgress />
                    <Component {...pageProps} />
                    <Toaster position='top-center' dir={dir} offset={16} />
                </div>
            </TooltipProvider>
        </DirectionProvider>
    )
}

export default App
